import React from 'react';

import { DataTable, Label, type TableColumn, ErrorMessage, Loading } from '@/src/ui';
import type { ExamHistoryExamTypeDTO, ExamHistoryStudentDTO } from '../models/examHistoryDTOs';
import { ReportCellStyle, ReportHeaderStyle, Constants } from '@/src/utils';

interface ExamHistoryTableProps {
    data: ExamHistoryExamTypeDTO[] | ExamHistoryStudentDTO[];
    viewMode: string;
    isLoading: boolean;
    error: string;
}

export default function ExamHistoryTable({
    data,
    viewMode,
    isLoading,
    error,
}: ExamHistoryTableProps) {
    const [hasSearched, setHasSearched] = React.useState(false);
    React.useEffect(() => {
        if (isLoading) setHasSearched(true);
    }, [isLoading]);
    if (isLoading) return <Loading message="Loading Exam History Report..." />;
    if (error) return <ErrorMessage error={error} />;
    if ((!data || !data.length) && hasSearched) return <div>No data available.</div>;
    if (!hasSearched) return null;

    let columns: TableColumn[] = [];
    if (viewMode === 'exam') {
        const BandBar: React.FC<{ bands: Record<string, number> }> = ({ bands }) => {
            const preferredOrder = [
                'Not Met',
                'Below',
                'Near',
                'At/Above',
                'Nearly',
                'Met',
                'Exceeds',
            ];
            const keys = preferredOrder.filter((key) => key in bands);

            const bandKeys = keys.length > 0 ? keys : Object.keys(bands);
            const total = bandKeys.reduce((sum, key) => sum + (bands[key] ?? 0), 0);
            return (
                <div
                    style={{
                        display: 'flex',
                        height: 18,
                        width: '100%',
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: 'inset 0 0 2px #ccc',
                    }}
                >
                    {bandKeys.map((key) => {
                        const percent = total > 0 ? bands[key] : 0;
                        return (
                            <div
                                key={key}
                                style={{
                                    width: `${percent}%`,
                                    background: Constants.bandColors[key],
                                    height: '100%',
                                }}
                                title={`${key}: ${percent}%`}
                            />
                        );
                    })}
                </div>
            );
        };

        let bandNames: string[] = [];
        if (data[0]) {
            const firstRow = data[0] as any;
            if (firstRow.bands && typeof firstRow.bands === 'object') {
                bandNames = Object.keys(firstRow.bands);
            } else {
                const possibleBandKeys = [
                    'Not Met',
                    'Near',
                    'At/Above',
                    'Nearly',
                    'Met',
                    'Exceeds',
                    'Below',
                ];
                bandNames = possibleBandKeys.filter((key) => key in firstRow);
                if (bandNames.length === 0) {
                    bandNames = ['PercentBelow', 'PercentNear', 'PercentAtAbove'];
                }
            }

            if (bandNames.includes('Not Met') || bandNames.includes('Below')) {
                bandNames = bandNames.filter((b) => b !== 'Not Met' && b !== 'Below');
                bandNames.unshift('Not Met / Below');
            }
        }

        const colorMap: Record<string, string> = Constants.bandColors;
        columns = [
            {
                dataKey: 'examName',
                header: 'Assessment',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'studentsTested',
                header: 'Students Tested',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamHistoryExamTypeDTO) => {
                    if (row.studentsTested === null) return 'N/A';
                    if (row.studentsTested === 0) return '0';
                    return row.studentsTested;
                },
            },
            {
                dataKey: 'averageScore',
                header: 'Average Score',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamHistoryExamTypeDTO) => {
                    if (row.averageScore === 0) return '0';
                    if (row.averageScore === null || row.averageScore === undefined) return 'N/A';
                    return `${row.averageScore}%`;
                },
            },
            ...bandNames.map((band) => ({
                dataKey: band,
                header: band,
                align: 'center' as const,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: any) => {
                    let value;
                    if (band === 'Not Met / Below') {
                        if (row.bands && typeof row.bands === 'object') {
                            value = row.bands['Not Met'] ?? row.bands['Below'] ?? '';
                        } else {
                            value = row['Not Met'] ?? row['Below'] ?? row['PercentBelow'] ?? '';
                        }
                        return (
                            <Label color={colorMap['Not Met'] || colorMap['Below'] || undefined}>
                                {value !== '' ? value : ''}%
                            </Label>
                        );
                    } else {
                        if (row.bands && typeof row.bands === 'object' && band in row.bands) {
                            value = row.bands[band];
                        } else if (row[band] !== undefined) {
                            value = row[band];
                        } else {
                            value = row[band as keyof ExamHistoryExamTypeDTO];
                        }
                        return (
                            <Label color={colorMap[band] || undefined}>
                                {value !== undefined && value !== null ? value : ''}%
                            </Label>
                        );
                    }
                },
            })),
            {
                dataKey: 'BandBar',
                header: 'Band Distribution',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: any) => {
                    let bands: Record<string, number> = {};
                    if (row.bands && typeof row.bands === 'object') {
                        bands = row.bands;
                    } else {
                        bands = {
                            'Not Met': row['Not Met'] ?? row['PercentBelow'] ?? 0,
                            Near: row['Near'] ?? row['PercentNear'] ?? 0,
                            'At/Above': row['At/Above'] ?? row['PercentAtAbove'] ?? 0,
                        };
                    }
                    return <BandBar bands={bands} />;
                },
            },
        ];
    } else {
        const colorMap: Record<string, string> = Constants.bandColors;
        columns = [
            {
                dataKey: 'studentName',
                header: 'Student Name',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'attemptNumber',
                header: 'Attempt #',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'attemptDate',
                allowSort: true,
                header: 'Date',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamHistoryStudentDTO) => {
                    const value = row.attemptDate;
                    if (!value) return '';
                    const date = new Date(value);
                    if (isNaN(date.getTime())) return value;
                    return date.toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    });
                },
            },
            {
                dataKey: 'score',
                header: 'Score',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamHistoryStudentDTO) => {
                    if (row.score === null || row.score === undefined) return 'N/A';
                    if (row.score === 0) return '0';
                    return `${row.score}%`;
                },
            },
            {
                dataKey: 'ReportingCategory',
                header: 'Band',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamHistoryStudentDTO) => {
                    const bandValue = row.reportingCategory;
                    return <Label color={colorMap[bandValue] || undefined}>{bandValue}</Label>;
                },
            },
            {
                dataKey: 'examName',
                allowSort: true,
                header: 'Assessment',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examTypeName',
                header: 'Assessment Type',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
        ];
    }

    const defaultSortColumn = viewMode === 'exam' ? 'examName' : 'studentName';

    return (
        <DataTable
            columns={columns}
            data={data}
            tableName={
                viewMode === 'exam' ? 'Exam History (By Assessment)' : 'Exam History (By Student)'
            }
            rowsPerPageInitial={50}
            enableExportCSV
            alternatingRowColor
            defaultSortColumn={defaultSortColumn}
            defaultSortDirection="asc"
        />
    );
}
